import React from 'react'
import moment from 'moment'
import { Set } from 'immutable'

// @src imports
import {
  useAccount,
  useEffect,
  useLocalStorage,
  useMutations,
  usePersistedState,
  usePersistedUserData,
  useQueries,
} from 'src/hooks'

import useActions from 'src/hooks/useActions'
import { Category as ItemCategory, Item } from 'src/campaign_store/types'
import { ACCOUNT_TOOLS_ID } from 'src/app/constants'

import {
  AffiliatePaymentInfo,
  ItemsPaymentInfo,
  PurchaseTypes,
} from 'src/payments/containers/Payment'

import { JoinModal } from 'src/user/components'

import { Confirm, DefaultError, NavLink, Transition } from 'src/chrome'

import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'

import { getItemCategory, PlanFragment } from 'src/graphql'

import { PLAN_ORDER } from 'src/marketing/plans/constants'

import { PlanDrawer } from 'src/marketing/plans/components'
import { PaymentModal } from 'src/payments/components'
import { noop } from 'src/helpers/appHelpers'

// relative imports
import { Plan } from '../types'

import {
  AccountStorage,
  AffiliateForm,
  AutomatedGSendsAndCampaignGrid,
  ContentSplitter,
  PlanContentsGrid,
} from '../components'

import { AffiliateFormInfo } from '../components/AffiliateForm/AffiliateForm'
import { useState } from '../../../hooks'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import WaysToSend from '../components/WaysToSend/WaysToSend'
import CampaignStoreAddOn from '../components/CampaignStoreAddOn/CampaignStoreAddOn'
import Plans from 'src/plans/containers/Plans'
import {
  Button,
  ConfirmDialog,
  Dialog,
  Div,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  bundlePlans,
  enabledPlans as plans,
} from 'src/marketing/plans/plansInfo'
import BasicPlan from '../components/BasicPlan/BasicPlan'

const styles = {
  pricingContainer: {
    width: '100%',
    fontFamily: "'Open Sans', sans-serif",
  },
}

type PlansProps = {
  route?: PricingRoute
}

type Dialogs =
  | 'updateUpgradeBundle'
  | 'updateDowngradeBundle'
  | 'downgradeFromBundle'
  | 'planDrawer'
  | 'paymentModal'

type State = {
  accountTools?: ItemCategory | null
  activeItem?: Item | null
  upgradeConfirm: boolean
  isCancelConfirmationVisible: boolean
  isKeepSubscriptionVisible: boolean
  dialog?: Dialogs
  shouldUpdateBundle: boolean
  isCanceling: boolean
  downgrade?: string
  selectedPlan?: Plan
  selectedItem: Item | null
  purchasing?: boolean
  paymentInfo?: ItemsPaymentInfo | AffiliatePaymentInfo
  isAffiliate: boolean
  isJoinModalOpen: boolean
  next: string | null
}

const initialState: State = {
  upgradeConfirm: false,
  isCancelConfirmationVisible: false,
  isKeepSubscriptionVisible: false,
  dialog: undefined,
  shouldUpdateBundle: false,
  isCanceling: false,
  selectedItem: null,
  purchasing: false,
  isAffiliate: false,
  isJoinModalOpen: false,
  next: null,
}

const Pricing: React.FC<PlansProps> = props => {
  const [state, _setState] = usePersistedState('join-page', initialState)

  const setState = (updates: Partial<State>) =>
    _setState(state => ({ ...(state ?? initialState), ...updates }))

  const resetState = () =>
    _setState(state => ({
      ...(state ?? initialState).accountTools,
      ...initialState,
    }))

  const {
    accountTools,
    activeItem,
    isCancelConfirmationVisible,
    isKeepSubscriptionVisible,
    dialog,
    shouldUpdateBundle,
    downgrade,
    selectedPlan,
    paymentInfo,
    isAffiliate,
    isJoinModalOpen,
    next,
  } = state

  const { route } = props

  const [planTitle, setPlanTitle] = useState<string>('Enhanced')
  const [addOnTitles, setAddOnTitles] = useState<Set<string>>(Set())

  const actions = useActions()
  const storage = useLocalStorage()

  const { controlData } = usePersistedUserData()
  const account = useAccount()

  const mutations = useMutations()

  const [itemCategory] = useQueries(getItemCategory({ id: ACCOUNT_TOOLS_ID }))

  useEffect(() => {
    loadAccountTools()
    if (route?.affiliate) {
      setState({ isAffiliate: true })
    }
    if (route?.plan !== undefined) {
      showPlanUpgrade(route.plan)
    }

    return () => {
      storage?.removeItem('join-page')
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  const handleAffiliate = () => {
    const hasAccount = !!account.username
    setState({
      isAffiliate: hasAccount,
      isJoinModalOpen: !hasAccount,
      next: !hasAccount ? 'handleAffiliate' : null,
    })
  }

  const onCloseModal = () => resetState()

  const onFinishJoin = () => {
    if (next) {
      if (
        next === 'showPlanUpgrade' &&
        selectedPlan &&
        selectedPlan.title === 'Basic'
      ) {
        window.location.reload(false)
      } else if (next === 'handleAffiliate') {
        handleAffiliate()
      }
    }

    setState({ next: null })
  }

  const closeCancel = () => resetState()

  const confirmCancel = async () => {
    setState({
      isCancelConfirmationVisible: false,
      isKeepSubscriptionVisible: false,
      dialog: undefined,
      isCanceling: true,
    })

    try {
      const {
        updateAccount: { account },
      } = await mutations.updateAccount({
        account: {
          plan: selectedPlan?.id ?? '1',
          keepBundlePlan: shouldUpdateBundle,
        },
      })
      actions.updatedAccount?.(account)
      resetState()
    } catch (err) {
      handlePurchaseError()
    }
  }

  const confirmCancelAndKeepStreamMarketing = async () => {
    setState({
      isCancelConfirmationVisible: false,
      isKeepSubscriptionVisible: false,
      dialog: undefined,
      isCanceling: true,
    })
    try {
      const {
        updateAccount: { account },
      } = await mutations.updateAccount({
        account: {
          plan: selectedPlan?.id ?? '1',
          keepStreamMarketing: true,
        },
      })
      actions.updatedAccount?.(account)
      resetState()
    } catch (err) {
      handlePurchaseError()
    }
  }

  const confirmBundleUpdate = () => {
    if (selectedPlan) {
      setPlanTitle(selectedPlan.title)
      setAddOnTitles(Set())
      setState({
        dialog: 'planDrawer',
        shouldUpdateBundle: true,
      })
    }
  }

  const showPlanUpgrade = (planTitle: string, isKeepButton?: boolean) => {
    const plan = plans.find(plan => plan.title === planTitle) ?? selectedPlan

    if (!plan) {
      return
    }

    if (isKeepButton) {
      setState({
        isKeepSubscriptionVisible: true,
        selectedPlan: plan,
        shouldUpdateBundle: account.hasBundlePlan,
      })
      return
    }

    const canDowngrade =
      plan &&
      PLAN_ORDER.indexOf(plan.stripeId) <
        PLAN_ORDER.indexOf(account.plan.stripeId)

    if (account.hasBundlePlan) {
      updateBundleAttempt(!canDowngrade, plan)
      return
    }

    if (canDowngrade) {
      setState({
        downgrade: plan.title,
        isCancelConfirmationVisible: true,
        selectedPlan: plan,
      })
    } else {
      setPlanTitle(plan.title)
      setAddOnTitles(Set())
      setState({
        dialog: 'planDrawer',
      })
    }
  }

  const updateBundleAttempt = (isUpgrading: boolean, plan: PlanFragment) => {
    // Downgrading
    if (!isUpgrading) {
      if (plan.stripeId === 'limited' || plan.stripeId === '14547') {
        setState({
          dialog: 'downgradeFromBundle',
          selectedPlan: plan,
        })
      } else {
        setState({
          dialog: 'updateDowngradeBundle',
          shouldUpdateBundle: true,
          selectedPlan: bundlePlans.find(p => p.id === plan.id),
        })
      }
      // Upgrading
    } else {
      setState({
        dialog: 'updateUpgradeBundle',
        selectedPlan: bundlePlans.find(p => p.id === plan.id),
      })
    }
  }

  const loadAccountTools = () => {
    setState({
      accountTools: {
        ...itemCategory,
        children: itemCategory.children.map(child => ({
          ...child,
          children: [],
        })),
      },
    })
  }

  const purchase = (item: Item) => {
    setState({
      activeItem: null,
      dialog: 'paymentModal',
      paymentInfo: {
        items: [item],
        purchaseType: PurchaseTypes.ITEMS,
      },
    })
  }

  const purchaseAffiliate = (data: AffiliateFormInfo) => {
    setState({
      isAffiliate: false,
      dialog: 'paymentModal',
      paymentInfo: {
        data,
        purchaseType: PurchaseTypes.DISTRIBUTOR,
      },
    })
  }

  const handlePurchaseSuccess = () => {
    if (next) {
      window.location.reload(false)
    }
  }

  const handlePurchaseError = () => resetState()

  const showProductPreview = (item: Item) =>
    setState({
      activeItem: item,
    })

  const closeProductPreview = () => setState({ activeItem: null })

  useEffect(() => {
    if (dialog === 'planDrawer') {
      document.body.style.overflow = 'hidden'
    }

    if (dialog !== 'planDrawer') {
      document.body.style.overflow = 'unset'
    }
  }, [dialog])

  const subscription = account.stripeSubscription

  return (
    <Div>
      <PaymentModal
        isOpen={controlData.isLoggedIn && dialog === 'paymentModal'}
        paymentInfo={paymentInfo}
        onClose={() => setState({ dialog: undefined, paymentInfo: undefined })}
        onSuccess={
          paymentInfo?.purchaseType === PurchaseTypes.DISTRIBUTOR
            ? handlePurchaseSuccess
            : noop
        }
        onError={
          paymentInfo?.purchaseType === PurchaseTypes.DISTRIBUTOR
            ? handlePurchaseError
            : noop
        }
      />
      <Div css={styles.pricingContainer}>
        <Plans onSelect={showPlanUpgrade} />
        <BasicPlan onSelect={showPlanUpgrade} />
        <WaysToSend />
        <PlanContentsGrid />
        <AutomatedGSendsAndCampaignGrid />
        <AccountStorage />
        <CampaignStoreAddOn
          accountTools={accountTools}
          activeItem={activeItem}
          purchase={purchase}
          showProductPreview={showProductPreview}
          closeProductPreview={closeProductPreview}
        />
        {account && (
          <>
            <NavLink onClick={() => actions.openCampaignStore()}>
              <ContentSplitter
                title={'View Campaign Store'}
                description={
                  'We have campaigns for just about every niche you can think of.'
                }
                icon={{
                  icon: 'CART',
                  size: 30,
                  viewBox: '0 0 35 32',
                  color: '#fff',
                }}
              />
            </NavLink>
            <Spacer space="x8" />
          </>
        )}
        {isCancelConfirmationVisible && (
          <Confirm
            title={`Downgrade ${account.plan.title} Subscription`}
            message={`
              Your subscription will be downgraded to ${downgrade} at the end of the current billing period:
              ${
                subscription &&
                moment(subscription.currentPeriodEnd).format(
                  'ddd MMM DD, YYYY HH:mm a',
                )
              }
            `}
            confirmTitle={'Continue'}
            onConfirm={confirmCancel}
            onDecline={closeCancel}
            isModalEnabled={true}
          />
        )}
        {isKeepSubscriptionVisible && (
          <Confirm
            title={`Keep current subscription`}
            message={`
              Do not change my subscription. I want to keep my current subscription
            `}
            confirmTitle={'Confirm'}
            onConfirm={confirmCancel}
            onDecline={closeCancel}
            isModalEnabled={true}
          />
        )}
        {dialog === 'downgradeFromBundle' && (
          <Dialog isOpen={true}>
            <Text type="body">
              By changing your SendOutCards subscription, you will lose your
              bundled pricing. Would you like to keep your Stream Marketing
              subscription as a separate subscription?
            </Text>
            <Spacer space="x4" />
            <Div outset="auto">
              <Button onClick={() => confirmCancelAndKeepStreamMarketing()}>
                Downgrade SendOutCards and <b>Keep Stream Marketing</b>
              </Button>
            </Div>
            <Spacer space="x3" />
            <Div outset="auto">
              <Button onClick={() => confirmCancel()}>
                Downgrade SendOutCards and <b>Cancel Stream Marketing</b>
              </Button>
            </Div>
            <Spacer space="x3" />
            <Div outset="auto">
              <Button onClick={() => closeCancel()}>
                Keep my Current Subscription
              </Button>
            </Div>
          </Dialog>
        )}
        {selectedPlan && dialog === 'updateUpgradeBundle' && (
          <ConfirmDialog
            isOpen={true}
            onClose={closeCancel}
            zIndex={4}
            title="Upgrade subscription bundle"
            description={`By upgrading your SendOutCards subscription, you are changing your bundled subscription plan, and keeping Stream Marketing. Would you like to upgrade your bundled subscription to 
              ${selectedPlan.price.description}?
            `}
            accept={{
              title: 'Yes, upgrade my subscription.',
              onClick: confirmBundleUpdate,
            }}
            decline={{
              title: 'No, keep my current subscription.',
              onClick: closeCancel,
            }}
          />
        )}
        {selectedPlan && dialog === 'updateDowngradeBundle' && (
          <ConfirmDialog
            isOpen={true}
            onClose={closeCancel}
            zIndex={4}
            title="Downgrade subscription bundle"
            description={`By changing your SendOutCards subscription, you are also changing your bundled subscription plan. Would you like to downgrade your bundle to the new price of   
              ${selectedPlan.price.description}?
            `}
            accept={{
              title: 'Yes, downgrade my subscription.',
              onClick: confirmCancel,
            }}
            decline={{
              title: 'No, keep my current subscription.',
              onClick: closeCancel,
            }}
          />
        )}
        {isAffiliate && (
          <AffiliateForm close={onCloseModal} purchase={purchaseAffiliate} />
        )}
        {isJoinModalOpen && (
          <JoinModal
            initialAction={'join'}
            onClose={onCloseModal}
            onAfterSave={onFinishJoin}
          />
        )}
        {dialog === 'planDrawer' && (
          <PlanDrawer
            onClose={resetState}
            planTitle={planTitle}
            addOnTitles={addOnTitles}
            shouldUpdateBundle={shouldUpdateBundle}
          />
        )}
      </Div>
    </Div>
  )
}

export default suspenseBoundary({
  component: Pricing,
  unresolved: <Transition message={'Loading...'} />,
  failure: DefaultError,
})
